import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useDndMonitor } from "@dnd-kit/core";
import {
  Box,
  EmptyState,
  Icon,
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
  Separator,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type IconName,
} from "@salestrak/ui";

import {
  useLinkOptions,
  type CreateFieldDoor,
} from "#app/components/forms/form-builder/form-answer-link";
import { usePaletteDrag } from "#app/components/forms/form-builder/form-builder-dnd";
import {
  PaneScrollGutter,
  PaneScroller,
} from "#app/components/forms/form-builder/settings-rows";
import { DisabledReason } from "#app/components/menus/disabled-reason";
import { FieldsPickerHeader } from "#app/components/view-options/add-field-page";
import { SectionHeader } from "#app/components/view-options/display-field-rows";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import {
  FORM_PROPERTY_FIELD_TYPE,
  FORM_QUESTION_FIELD_TYPE,
  FORM_QUESTION_KINDS,
  FORM_QUESTION_LABEL,
  makeCustomFieldItem,
  makeFormItem,
  makePropertyItem,
  questionKindForFieldType,
  type Form,
  type FormItem,
  type FormQuestionKind,
} from "#app/lib/forms";

/** One Project slot with two writers races on Submit. */
const ALREADY_LINKED = "Already on this form";

const NO_ITEMS: Pick<Form, "items"> = { items: [] };

function EntryGlyph({ entry }: { entry: FormPaletteEntry }) {
  return (
    <Box align="center" flex="none" justify="center" width={24}>
      <Icon color={entry.iconColor ?? "muted"} name={entry.icon} size="md" />
    </Box>
  );
}

function PaletteRowFace({ entry, trailing }: { entry: FormPaletteEntry; trailing: ReactNode }) {
  return (
    <>
      <Box align="center" flex="none" justify="center" width={16}>
        <Icon color={entry.iconColor ?? "muted"} name={entry.icon} size="md" />
      </Box>
      <Box direction="column" fill="flex" minWidth={0} shrink>
        <Text size="sm" truncate={1}>
          {entry.label}
        </Text>
      </Box>
      {trailing}
    </>
  );
}

function rowProps(disabled: boolean, isDragging: boolean) {
  return {
    align: "center",
    "aria-disabled": disabled || undefined,
    cursor: disabled ? undefined : isDragging ? "grabbing" : "grab",
    direction: "row",
    fill: "width",
    gap: "sm",
    group: "row",
    hoverBackground: !disabled && !isDragging,
    minHeight: 32,
    opacity: disabled ? 50 : isDragging ? 40 : undefined,
    paddingX: "sm",
    pressFeedback: !disabled,
    rounded: "sm",
  } as const;
}

function PaletteRow({ entry, onClick }: { entry: FormPaletteEntry; onClick: () => void }) {
  const { ref, dragProps, isDragging } = usePaletteDrag(entry);
  const disabled = Boolean(entry.disabledReason);
  const row = (
    <Box<"button">
      ref={ref}
      {...dragProps}
      {...rowProps(disabled, isDragging)}
      as="button"
      // The drop fires a click on the row the drag started from; a carried shape has already landed.
      onClick={disabled || isDragging ? undefined : onClick}
    >
      <PaletteRowFace
        entry={entry}
        trailing={
          disabled ? null : (
            <Box hoverReveal="row">
              <Icon color="faint" name="plus" size="sm" />
            </Box>
          )
        }
      />
    </Box>
  );
  return entry.disabledReason ? (
    <DisabledReason reason={entry.disabledReason}>{row}</DisabledReason>
  ) : (
    row
  );
}

function LinkMenuItem({
  entry,
  scope,
  bare = false,
  onAdd,
}: {
  entry: FormPaletteEntry;
  scope?: string | undefined;
  bare?: boolean;
  onAdd: () => void;
}) {
  const { ref, listeners, isDragging } = usePaletteDrag(entry, scope ?? "inert", !scope);
  const disabled = Boolean(entry.disabledReason);
  return (
    <MenuItem
      {...listeners}
      className={disabled || !scope ? undefined : "cursor-grab"}
      disabled={disabled}
      disabledReason={entry.disabledReason ?? undefined}
      onClick={isDragging ? undefined : onAdd}
      ref={ref}
      size={bare ? undefined : "lg"}
    >
      {bare ? null : <EntryGlyph entry={entry} />}
      <Text size={null} truncate={1}>
        {entry.label}
      </Text>
    </MenuItem>
  );
}

function FamilyMenuGroups({
  shapes,
  properties,
  fields,
  onAdd,
  onCreateField,
  scope,
}: {
  shapes: FormPaletteEntry[];
  properties: FormPaletteEntry[];
  fields: FormPaletteEntry[];
  onAdd: (item: FormItem) => void;
  onCreateField: CreateFieldDoor;
  scope?: string | undefined;
}) {
  const creatable = onCreateField ? shapes.filter((shape) => !shape.disabledReason) : [];
  const create = (shape: FormPaletteEntry) => () =>
    onCreateField?.(
      shape.kind === "attachment" ? "photos" : FORM_QUESTION_FIELD_TYPE[shape.kind as FormQuestionKind],
      (created) => onAdd(makeCustomFieldItem(created)),
    );
  const ordered = shapes.flatMap((shape) => fields.filter((option) => option.kind === shape.kind));
  const linkItem = (option: FormPaletteEntry) => (
    <LinkMenuItem
      bare
      entry={option}
      key={option.key}
      onAdd={() => onAdd(option.mint())}
      scope={scope}
    />
  );
  const newFieldDoor = () => {
    const [only] = creatable;
    if (!only) return null;
    const plusClass =
      "mb-1.5 size-5 min-h-5 justify-center rounded-full border p-0 text-muted-foreground data-highlighted:text-foreground";
    const face = <Icon className="size-3" name="plus" />;
    const withTip = (trigger: React.ReactElement) => (
      <Tooltip>
        <TooltipTrigger render={trigger} />
        <TooltipPopup side="top">New Custom Field</TooltipPopup>
      </Tooltip>
    );
    if (creatable.length === 1) {
      return withTip(
        <MenuItem aria-label="New Custom Field" className={plusClass} onClick={create(only)}>
          {face}
        </MenuItem>,
      );
    }
    return (
      <MenuSub>
        {withTip(
          <MenuSubTrigger
            aria-label="New Custom Field"
            chevron={false}
            className={plusClass}
            openOnHover={false}
          >
            {face}
          </MenuSubTrigger>,
        )}
        <MenuSubPopup autoHighlight sheetTitle="New Custom Field" width="panel">
          <MenuGroup>
            <MenuGroupLabel>New Custom Field</MenuGroupLabel>
            {creatable.map((shape) => (
              <MenuItem key={shape.key} onClick={create(shape)} size="lg">
                <EntryGlyph entry={shape} />
                <Text size={null}>{shape.label}</Text>
              </MenuItem>
            ))}
          </MenuGroup>
        </MenuSubPopup>
      </MenuSub>
    );
  };
  return (
    <>
    <MenuGroup aria-label="Add to form">
      {shapes.map((shape) => (
        <LinkMenuItem
          entry={shape}
          key={shape.key}
          onAdd={() => onAdd(shape.mint())}
          scope={scope}
        />
      ))}
    </MenuGroup>
    {properties.length > 0 ? (
      <>
        <MenuSeparator />
        <MenuGroup>
          <MenuGroupLabel>Link to Property</MenuGroupLabel>
          {properties.map((option) => (
            <LinkMenuItem
              entry={option}
              key={option.key}
              onAdd={() => onAdd(option.mint())}
              scope={scope}
            />
          ))}
        </MenuGroup>
      </>
    ) : null}
    {fields.length > 0 || creatable.length > 0 ? (
      <>
        <MenuSeparator />
        <MenuGroup>
          {/* The door rides the heading as a plus, so the fields lead
              and the act of making one costs no row of its own. */}
          <Box align="center" direction="row" justify="between">
            <MenuGroupLabel>Link to Custom Field</MenuGroupLabel>
            {newFieldDoor()}
          </Box>
          {ordered.map(linkItem)}
        </MenuGroup>
      </>
    ) : null}    </>
  );
}

function ShapeRow({
  entry,
  shapes,
  properties,
  fields,
  onAdd,
  onCreateField,
}: {
  entry: FormPaletteEntry;
  shapes: FormPaletteEntry[];
  properties: FormPaletteEntry[];
  fields: FormPaletteEntry[];
  onAdd: (item: FormItem) => void;
  onCreateField: CreateFieldDoor;
}) {
  const { ref, dragProps, isDragging } = usePaletteDrag(entry);
  const [open, setOpen] = useState(false);
  // The menu must outlive the drag it started, so it stays mounted, hidden, and shuts on the drop.
  const scope = `menu:${entry.key}`;
  const [carrying, setCarrying] = useState(false);
  const putDown = () => {
    if (!carrying) return;
    setCarrying(false);
    setOpen(false);
  };
  useDndMonitor({
    onDragCancel: putDown,
    onDragEnd: putDown,
    onDragStart: ({ active }) => {
      if (String(active.id).startsWith(`form-palette:${scope}:`)) setCarrying(true);
    },
  });
  const disabled = Boolean(entry.disabledReason);
  const row = (
    <Menu modal={false} onOpenChange={setOpen} open={(open || carrying) && !isDragging}>
      <MenuTrigger
        disabled={disabled}
        openOnHover
        render={
          <Box<"button"> ref={ref} {...dragProps} {...rowProps(disabled, isDragging)} as="button">
            {/* The rail is neutral, as the layout rows are: the hues belong
                to the shapes in the menu, where they tell one apart from
                the next. */}
            <PaletteRowFace
              entry={{ ...entry, iconColor: undefined }}
              trailing={disabled ? null : <Icon color="faint" name="chevron-right" size="sm" />}
            />
          </Box>
        }
      />
      {/* The palette is the dialog's right column, so the menu opens toward
          the canvas. */}
      <MenuPopup
        align="start"
        autoHighlight
        className={carrying ? "invisible" : undefined}
        side="inline-start"
        width="panel"
      >
        <FamilyMenuGroups
          fields={fields}
          onAdd={onAdd}
          onCreateField={onCreateField}
          properties={properties}
          scope={scope}
          shapes={shapes}
        />
      </MenuPopup>
    </Menu>
  );
  return entry.disabledReason ? (
    <DisabledReason reason={entry.disabledReason}>{row}</DisabledReason>
  ) : (
    row
  );
}

const SHAPE_FAMILIES: {
  label: string;
  kinds: FormPaletteEntry["kind"][];
}[] = [
  { kinds: ["shortText", "longText"], label: "Text" },
  { kinds: ["singleChoice", "multipleChoice", "yesNo"], label: "Choice" },
  { kinds: ["number", "money"], label: "Number" },
  { kinds: ["date", "time"], label: "Date & time" },
  { kinds: ["email", "phone", "url"], label: "Contact" },
];

const familyOf = (kind: FormPaletteEntry["kind"]) =>
  SHAPE_FAMILIES.find((family) => family.kinds.includes(kind));

function familyRows(
  questions: FormPaletteEntry[],
): { entry: FormPaletteEntry; shapes: FormPaletteEntry[] }[] {
  const rows: { entry: FormPaletteEntry; shapes: FormPaletteEntry[] }[] = [];
  const placed = new Set<string>();
  for (const question of questions) {
    if (placed.has(question.kind)) continue;
    const family = familyOf(question.kind);
    const shapes = family
      ? questions.filter((shape) => family.kinds.includes(shape.kind))
      : [question];
    for (const shape of shapes) placed.add(shape.kind);
    if (!family || shapes.length === 1) {
      rows.push({ entry: question, shapes });
      continue;
    }
    rows.push({
      entry: {
        ...question,
        disabledReason: shapes.every((shape) => shape.disabledReason)
          ? question.disabledReason
          : null,
        key: `family:${family.label}`,
        label: family.label,
      },
      shapes,
    });
  }
  return rows;
}

function PaletteSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box aria-label={label} direction="column" gap="2xs" paddingX="md" paddingY="sm" role="list">
      <SectionHeader label={label} />
      {children}
    </Box>
  );
}

export type FormPaletteEntry = {
  key: string;
  kind: FormQuestionKind | "attachment" | "section" | "text";
  label: string;
  icon: IconName;
  iconColor?: React.ComponentProps<typeof Icon>["color"];
  disabledReason: string | null;
  mint: () => FormItem;
};

export function useFormPaletteEntries(): {
  questions: FormPaletteEntry[];
  layout: FormPaletteEntry[];
} {
  return useMemo(() => {
    const photos = getFieldTypeMeta("photos");
    const shape = (
      kind: FormPaletteEntry["kind"],
      entry: Omit<FormPaletteEntry, "key" | "kind" | "mint">,
    ): FormPaletteEntry => ({ ...entry, key: kind, kind, mint: () => makeFormItem(kind) });
    return {
      layout: [
        shape("section", { disabledReason: null, icon: "heading-2", label: "Section heading" }),
        shape("text", { disabledReason: null, icon: "text", label: "Text block" }),
      ],
      questions: [
        ...FORM_QUESTION_KINDS.map((kind) => {
          const meta = getFieldTypeMeta(FORM_QUESTION_FIELD_TYPE[kind]);
          return shape(kind, {
            disabledReason: null,
            icon: meta.icon,
            iconColor: meta.color,
            label: FORM_QUESTION_LABEL[kind],
          });
        }),
        shape("attachment", {
          disabledReason: null,
          icon: photos.icon,
          iconColor: photos.color,
          label: "Photos",
        }),
      ],
    };
  }, []);
}

function useExistingEntries(
  form: Pick<Form, "items">,
  photosReason: string | null,
): { properties: FormPaletteEntry[]; fields: FormPaletteEntry[] } {
  const every = useLinkOptions(NO_ITEMS, null);
  const open = useLinkOptions(form, null);
  return useMemo(() => {
    const offered = new Set(open(null).map((option) => option.value));
    const properties: FormPaletteEntry[] = [];
    const fields: FormPaletteEntry[] = [];
    for (const option of every(null)) {
      const kind = questionKindForFieldType(
        "property" in option ? FORM_PROPERTY_FIELD_TYPE[option.property] : option.field.type,
      );
      if (!kind) continue;
      const entry: FormPaletteEntry = {
        disabledReason: !offered.has(option.value)
          ? ALREADY_LINKED
          : kind === "attachment"
            ? photosReason
            : null,
        icon: option.icon,
        iconColor: option.iconColor,
        key: option.value,
        kind,
        label: option.label,
        mint:
          "property" in option
            ? () => makePropertyItem(option.property)
            : () => makeCustomFieldItem(option.field),
      };
      ("property" in option ? properties : fields).push(entry);
    }
    return { fields, properties };
  }, [every, open, photosReason]);
}

export function InsertMenuItems({
  form,
  onAdd,
  onCreateField,
}: {
  form: Pick<Form, "items">;
  onAdd: (item: FormItem) => void;
  onCreateField: CreateFieldDoor;
}) {
  const { questions, layout } = useFormPaletteEntries();
  const photosReason = questions.find((entry) => entry.kind === "attachment")?.disabledReason;
  const { properties, fields } = useExistingEntries(form, photosReason ?? null);
  return (
    <>
      <MenuGroup>
        <MenuGroupLabel>Questions</MenuGroupLabel>
        {familyRows(questions).map(({ entry, shapes }) => {
          const face = (
            <>
              <EntryGlyph entry={{ ...entry, iconColor: undefined }} />
              <Text size={null}>{entry.label}</Text>
            </>
          );
          const ofShapes = (option: FormPaletteEntry) =>
            shapes.some((shape) => shape.kind === option.kind);
          return (
            <MenuSub key={entry.key}>
              <MenuSubTrigger
                disabled={Boolean(entry.disabledReason)}
                disabledReason={entry.disabledReason ?? undefined}
                size="lg"
              >
                {face}
              </MenuSubTrigger>
              <MenuSubPopup autoHighlight sheetTitle={entry.label} width="panel">
                <FamilyMenuGroups
                  fields={fields.filter(ofShapes)}
                  onAdd={onAdd}
                  onCreateField={onCreateField}
                  properties={properties.filter(ofShapes)}
                  shapes={shapes}
                />
              </MenuSubPopup>
            </MenuSub>
          );
        })}
      </MenuGroup>
      <MenuSeparator />
      <MenuGroup>
        <MenuGroupLabel>Layout</MenuGroupLabel>
        {layout.map((entry) => (
          <MenuItem key={entry.key} onClick={() => onAdd(entry.mint())} size="lg">
            <EntryGlyph entry={entry} />
            <Text size={null}>{entry.label}</Text>
          </MenuItem>
        ))}
      </MenuGroup>
    </>
  );
}

export function FormItemPalette({
  form,
  onAddItem,
  onCreateField,
}: {
  form: Pick<Form, "items">;
  onAddItem: (item: FormItem) => void;
  onCreateField: CreateFieldDoor;
}) {
  const { questions, layout } = useFormPaletteEntries();
  const photosReason = questions.find((entry) => entry.kind === "attachment")?.disabledReason;
  const { properties, fields } = useExistingEntries(form, photosReason ?? null);
  const [query, setQuery] = useState("");

  const needle = query.trim().toLowerCase();
  const matching = (entries: FormPaletteEntry[]) =>
    entries.filter((entry) => entry.label.toLowerCase().includes(needle));
  const questionRows = needle
    ? matching(questions).map((entry) => ({ entry, shapes: [entry] }))
    : familyRows(questions);
  const groups = [
    { entries: questionRows.map((row) => row.entry), label: "Questions", shapes: true },
    { entries: matching(layout), label: "Layout", shapes: false },
    ...(needle
      ? [
          { entries: matching(properties), label: "Properties", shapes: false },
          { entries: matching(fields), label: "Custom Fields", shapes: false },
        ]
      : []),
  ].filter((group) => group.entries.length > 0);

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      <Box paddingTop="md">
        <FieldsPickerHeader
          hairline={false}
          onQueryChange={setQuery}
          onTabChange={() => {}}
          query={query}
          tab="create"
          tabs={false}
        />
      </Box>
      {/* Only the list scrolls: the search stays put above it, with no
          hairline — the list's top edge fades under it once scrolled. */}
      <PaneScroller>
<Box direction="column" paddingTop="xs">
        {groups.length === 0 ? (
          <EmptyState icon="search" media="circle" size="sm" title="No matching fields" />
        ) : (
          groups.map((group, index) => (
            <Fragment key={group.label}>
              {index > 0 ? <Separator /> : null}
              <PaletteSection label={group.label}>
                {group.entries.map((entry, rowIndex) => {
                  if (group.shapes) {
                    const { shapes } = questionRows[rowIndex]!;
                    const ofShapes = (option: FormPaletteEntry) =>
                      shapes.some((shape) => shape.kind === option.kind);
                    return (
                      <ShapeRow
                        entry={entry}
                        fields={fields.filter(ofShapes)}
                        key={entry.key}
                        onAdd={onAddItem}
                        onCreateField={onCreateField}
                        properties={properties.filter(ofShapes)}
                        shapes={shapes}
                      />
                    );
                  }
                  return (
                    <PaletteRow
                      entry={entry}
                      key={entry.key}
                      onClick={() => onAddItem(entry.mint())}
                    />
                  );
                })}
              </PaletteSection>
            </Fragment>
          ))
        )}
        <PaneScrollGutter />
      </Box>
</PaneScroller>
    </Box>
  );
}
